import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";

const API_URL = "https://heou1vaond.execute-api.us-west-1.amazonaws.com/Prod"
const path = "/offerings"

const apiHeaders = {
    "Content-Type": 'application/json',
    "x-api-key": "nMEyHpJKWa8ks2jCvOkJ6akQcQh6Oloz6G3FW7yZ"
}

const ALL_CATEGORY = '__all__';
const UNCATEGORIZED = 'Other services';

// "Initial" consultations / assessments are the entry point for a new customer,
// so any category holding one leads the rail regardless of how big it is.
const INITIAL_RE = /initial/i;
const CONSULT_RE = /consult/i;

// Fade-out duration before the grid content swaps. Matches the .op-results
// "out" transition below, and mirrors the 200ms hand-off used in ServicesPage.
const FADE_OUT_MS = 200;

// Business blurbs longer than this are cut at a word boundary and finished with
// an inline "Show more", so the header never pushes the grid off the first
// screen. Cutting in JS (rather than clamping with CSS) is what lets the link
// sit on the same line as the truncated sentence.
const BIO_COLLAPSE_CHARS = 200;

// Height of the fixed nav. The sticky toolbar hangs off the same number
// (`top: 74px`), and the branding swap fires when the page header passes
// underneath this line.
const NAV_HEIGHT = 74;

const OfferingsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [services, setServices] = useState([]);
    // Branding pulled off the first service record: avatar, name, blurb, and
    // the two tappable contact details.
    const [business, setBusiness] = useState(null);
    const [loading, setLoading] = useState(true);
    const [bioExpanded, setBioExpanded] = useState(false);

    // Filtering state.
    // `activeCategory` is what the pills highlight — it updates the instant you
    // tap, so the rail never feels laggy. `renderedCategory` is what the grid is
    // actually showing; it only catches up once the fade-out finishes, which is
    // what makes the swap read as a crossfade instead of a hard cut.
    const [query, setQuery] = useState('');
    const [activeCategory, setActiveCategory] = useState(ALL_CATEGORY);
    const [renderedCategory, setRenderedCategory] = useState(ALL_CATEGORY);
    const [fadeState, setFadeState] = useState('in');
    const fadeTimer = useRef(null);

    // Category rail scrolling (desktop arrows).
    const catsRef = useRef(null);
    const [catScroll, setCatScroll] = useState({ left: false, right: false });

    // Nav branding.
    // Once the big avatar + name in the page header scrolls up behind the nav,
    // the "Verca" wordmark hands off to a small version of the same branding, so
    // you always know whose page you're on however far down the grid you are.
    const brandRef = useRef(null);
    const [navBranded, setNavBranded] = useState(false);

    const serverName = business?.name || '';

    useEffect(() => {
        setLoading(true);
        setServices([]);
        setBusiness(null);
        setBioExpanded(false);
        setQuery('');
        setActiveCategory(ALL_CATEGORY);
        setRenderedCategory(ALL_CATEGORY);
        setFadeState('in');
        setNavBranded(false);

        const fetchOfferings = async () => {
            try {
                const params = { "searchHandle": id }

                const resp = await axios.get(`${API_URL}${path}`, {
                    headers: apiHeaders,
                    params: params
                });

                const list = resp.data.services || [];
                setServices(list);
                if (list.length > 0) {
                    const first = list[0];
                    setBusiness({
                        name: (first.serverName || '').trim(),
                        avatar: first.server_Avatar_URL || '',
                        description: (first.serverDescription || '').trim(),
                        number: first.serverNumber || '',
                        location: (first.serverLocation || '').trim()
                    });
                }
                setLoading(false);
            } catch (e) {
                console.log("Error fetching offerings", e);
                setLoading(false);
            }
        }

        fetchOfferings();
    }, [id]);

    // Don't leave a pending swap running after unmount.
    useEffect(() => () => clearTimeout(fadeTimer.current), []);

    // ── Nav branding hand-off ──
    // Watch the avatar + name row in the page header rather than a magic scroll
    // offset, so the swap lands exactly as the real branding disappears — which
    // stays correct whatever the blurb length or viewport does to the header.
    useEffect(() => {
        if (loading || !business) {
            setNavBranded(false);
            return;
        }
        const el = brandRef.current;
        if (!el) return;

        if (typeof IntersectionObserver === 'undefined') {
            // Older browsers: fall back to a plain scroll threshold.
            const onScroll = () => setNavBranded(window.scrollY > 140);
            onScroll();
            window.addEventListener('scroll', onScroll, { passive: true });
            return () => window.removeEventListener('scroll', onScroll);
        }

        const io = new IntersectionObserver(
            ([entry]) => setNavBranded(!entry.isIntersecting),
            { root: null, rootMargin: `-${NAV_HEIGHT}px 0px 0px 0px`, threshold: 0 }
        );
        io.observe(el);
        return () => io.disconnect();
    }, [loading, business]);

    // The nav branding doubles as the way back up to the full header.
    const scrollToTop = () => {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
    };

    // ── Category ordering ──
    // One rule, computed from the full service list rather than the filtered
    // one, so both the pills and the group headings agree and neither reshuffles
    // while you're typing in the search box.
    // Note: a category literally named "Other services" collides with the
    // uncategorized bucket and gets pinned last with it.
    const categoryRank = useMemo(() => {
        const stats = new Map();
        services.forEach((s) => {
            const name = (s.category || '').trim() || UNCATEGORIZED;
            const entry = stats.get(name) || { count: 0, hasInitial: false };
            entry.count += 1;
            if (INITIAL_RE.test(s.name || '') || CONSULT_RE.test(s.name || '')) entry.hasInitial = true;
            stats.set(name, entry);
        });
        return stats;
    }, [services]);

    // Order: categories with an "initial" service, then by size, then
    // alphabetical as a tiebreak so equal categories hold a stable position.
    const compareCategories = useCallback((a, b) => {
        if (a === UNCATEGORIZED) return 1;
        if (b === UNCATEGORIZED) return -1;
        const A = categoryRank.get(a) || { count: 0, hasInitial: false };
        const B = categoryRank.get(b) || { count: 0, hasInitial: false };
        if (A.hasInitial !== B.hasInitial) return A.hasInitial ? -1 : 1;
        return B.count - A.count || a.localeCompare(b);
    }, [categoryRank]);

    // ── Categories ──
    // The pills the business actually uses, each carrying a count so the tab
    // itself tells you how much is behind it.
    const categories = useMemo(() => (
        Array.from(categoryRank.entries())
            .filter(([name]) => name !== UNCATEGORIZED)
            .sort((a, b) => compareCategories(a[0], b[0]))
            .map(([name, { count }]) => ({ name, count }))
    ), [categoryRank, compareCategories]);

    // Fade the grid out, swap the category underneath, fade it back in.
    const handleCategorySelect = (category) => {
        if (category === activeCategory) return;
        clearTimeout(fadeTimer.current);
        setActiveCategory(category);
        setFadeState('out');
        fadeTimer.current = setTimeout(() => {
            setRenderedCategory(category);
            setFadeState('in');
        }, FADE_OUT_MS);
    };

    // ── Category rail scrolling ──
    // Which arrows are live depends on where the rail is scrolled to. The 1px
    // slack absorbs sub-pixel scrollLeft values so the right arrow doesn't
    // linger once you've reached the end of the rail.
    const updateCatScroll = useCallback(() => {
        const el = catsRef.current;
        if (!el) return;
        const max = el.scrollWidth - el.clientWidth;
        setCatScroll({ left: el.scrollLeft > 1, right: el.scrollLeft < max - 1 });
    }, []);

    useEffect(() => {
        const el = catsRef.current;
        if (!el) return;
        updateCatScroll();
        el.addEventListener('scroll', updateCatScroll, { passive: true });
        const ro = new ResizeObserver(updateCatScroll);
        ro.observe(el);
        return () => {
            el.removeEventListener('scroll', updateCatScroll);
            ro.disconnect();
        };
    }, [categories, loading, updateCatScroll]);

    const scrollCats = (dir) => {
        const el = catsRef.current;
        if (!el) return;
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        // Just under a full pane, so a pill or two stays visible as an anchor.
        const step = Math.max(240, el.clientWidth * 0.8);
        el.scrollBy({ left: dir * step, behavior: reduced ? 'auto' : 'smooth' });
    };

    // ── Filtering ──
    // Search filters live (no fade) so typing stays responsive; only the
    // category swap animates.
    const filteredServices = useMemo(() => {
        const q = query.trim().toLowerCase();
        return services.filter((s) => {
            if (renderedCategory !== ALL_CATEGORY) {
                if ((s.category || '').trim() !== renderedCategory) return false;
            }
            if (!q) return true;
            return [s.name, s.description, s.category]
                .filter(Boolean)
                .some((field) => String(field).toLowerCase().includes(q));
        });
    }, [services, query, renderedCategory]);

    // ── Grouping ──
    // Same shape as the productsMap in ProductShop: category -> items, rendered
    // as a heading followed by a grid. Sorted with the same comparator as the
    // pills, so the page order matches the rail order.
    const groupedServices = useMemo(() => {
        const groups = new Map();
        filteredServices.forEach((s) => {
            const key = (s.category || '').trim() || UNCATEGORIZED;
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(s);
        });
        return Array.from(groups.entries()).sort((a, b) => compareCategories(a[0], b[0]));
    }, [filteredServices, compareCategories]);

    // A heading per category only earns its space while browsing across
    // categories — when one is selected the pill already names it.
    const showGroupHeadings = renderedCategory === ALL_CATEGORY && groupedServices.length > 1;

    const isFiltering = query.trim().length > 0 || renderedCategory !== ALL_CATEGORY;

    const clearFilters = () => {
        setQuery('');
        handleCategorySelect(ALL_CATEGORY);
    };

    // ── Display helpers ──

    const getPriceDisplay = (service) => {
        if (service.price === 0) {
            return { main: 'Free', original: null };
        }
        if (service.discountedbool && service.discountedprice != null) {
            return {
                main: `$${Number(service.discountedprice).toFixed(2)}`,
                original: `$${Number(service.price).toFixed(2)}`
            };
        }
        return { main: `$${Number(service.price).toFixed(2)}`, original: null };
    };

    const getDiscountPercent = (service) => {
        if (!service.discountedbool || service.discountedprice == null || !service.price) return null;
        const pct = Math.round(100 - (Number(service.discountedprice) / Number(service.price)) * 100);
        return pct > 0 ? pct : null;
    };

    const handleSelect = (service) => {
        navigate(`/services/${service.productuid}`);
    };

    const formatPhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return '';
        const cleaned = String(phoneNumber).replace(/\D/g, '');
        if (cleaned.length === 11 && cleaned[0] === '1') {
            return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
        } else if (cleaned.length === 10) {
            return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
        }
        return phoneNumber;
    };

    // Hand the address to the device's native maps app where there is one, and
    // fall back to Google Maps in a new tab on desktop. Same behaviour as the
    // location card in ServicesPage.
    const openLocation = (rawLocation) => {
        const location = encodeURIComponent(rawLocation);
        const userAgent = navigator.userAgent;
        if (/iPad|iPhone|iPod/.test(userAgent)) {
            window.open(`maps://maps.google.com/maps?q=${location}`);
        } else if (/Android/.test(userAgent)) {
            window.open(`intent://maps.google.com/maps?q=${location}#Intent;scheme=https;package=com.google.android.apps.maps;end`);
        } else {
            window.open(`https://maps.google.com/maps?q=${location}`, '_blank');
        }
    };

    // Cut the blurb at the last word boundary before the limit so the inline
    // "Show more" never lands mid-word.
    const truncateBio = (text, max) => {
        if (text.length <= max) return text;
        const slice = text.slice(0, max);
        const lastSpace = slice.lastIndexOf(' ');
        const cut = lastSpace > max * 0.5 ? slice.slice(0, lastSpace) : slice;
        return cut.replace(/\s+$/, '');
    };

    // First letter of the business name, used when there's no avatar to show.
    const avatarInitial = serverName ? serverName.charAt(0).toUpperCase() : '';

    // ── Nav branding ──
    // A miniature of the page header that occupies the same slot as the Verca
    // wordmark. Tapping it returns you to the top, where the full branding is.
    const renderNavBrand = () => {
        if (!business) return null;

        return (
            <button
                type="button"
                className="op-nav-slot op-nav-brand"
                onClick={scrollToTop}
                tabIndex={navBranded ? 0 : -1}
                aria-hidden={!navBranded}
                aria-label={`${serverName || 'Business'} — back to top`}
            >
                {business.avatar ? (
                    <img
                        className="op-nav-brand-avatar"
                        src={business.avatar}
                        alt=""
                        loading="eager"
                    />
                ) : (
                    <span className="op-nav-brand-avatar op-nav-brand-avatar-fallback" aria-hidden="true">
                        {avatarInitial || <i className="fas fa-store"></i>}
                    </span>
                )}
                <span className="op-nav-brand-name">{serverName || 'Offerings'}</span>
            </button>
        );
    };

    // ── Business header ──
    // Circular avatar with the business name set large beside it, the blurb
    // running full width underneath, then phone and location as tappable chips
    // — the phone dials, the location opens the device's maps app.
    const renderBusinessHeader = () => {
        if (!business) return null;
        const bio = business.description;
        const bioIsLong = bio.length > BIO_COLLAPSE_CHARS;
        const bioCollapsed = bioIsLong && !bioExpanded;

        return (
            <header className="op-brand">
                <div className="op-brand-main" ref={brandRef}>
                    {business.avatar ? (
                        <img
                            className="op-brand-avatar"
                            src={business.avatar}
                            alt={serverName || 'Business'}
                            loading="eager"
                        />
                    ) : (
                        <div className="op-brand-avatar op-brand-avatar-fallback" aria-hidden="true">
                            {avatarInitial || <i className="fas fa-store"></i>}
                        </div>
                    )}

                    <h1 className="op-brand-name">{serverName || 'Offerings'}</h1>
                </div>

                {bio && (
                    <p className="op-brand-description">
                        {bioCollapsed ? (
                            <>
                                {truncateBio(bio, BIO_COLLAPSE_CHARS)}…{' '}
                                <button
                                    type="button"
                                    className="op-bio-toggle"
                                    onClick={() => setBioExpanded(true)}
                                    aria-expanded={false}
                                >
                                    Show more
                                </button>
                            </>
                        ) : (
                            <>
                                {bio}
                                {bioIsLong && (
                                    <>
                                        {' '}
                                        <button
                                            type="button"
                                            className="op-bio-toggle"
                                            onClick={() => setBioExpanded(false)}
                                            aria-expanded={true}
                                        >
                                            Show less
                                        </button>
                                    </>
                                )}
                            </>
                        )}
                    </p>
                )}

                {(business.number || business.location) && (
                    <div className="op-brand-contact">
                        {business.number && (
                            <a href={`tel:${business.number}`} className="op-contact-chip">
                                <i className="fas fa-phone"></i>
                                <span>{formatPhoneNumber(business.number)}</span>
                            </a>
                        )}
                        {business.location && (
                            <button
                                type="button"
                                className="op-contact-chip"
                                onClick={() => openLocation(business.location)}
                            >
                                <i className="fas fa-map-marker-alt"></i>
                                <span>{business.location}</span>
                            </button>
                        )}
                    </div>
                )}
            </header>
        );
    };

    const renderCard = (service, index) => {
        const price = getPriceDisplay(service);
        const isBooking = service.schedbool;
        const discount = getDiscountPercent(service);
        const bundleQty = service.bundleqty || 1;
        // All-day services are booked as nights rather than timed sessions, so
        // a minute duration would be meaningless for them.
        const durationLabel = service.alldaybool
            ? 'Overnight'
            : (service.schedduration ? `${service.schedduration} min` : null);

        return (
            <div
                key={service.productuid || index}
                className="offering-card"
                role="button"
                tabIndex={0}
                onClick={() => handleSelect(service)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSelect(service);
                    }
                }}
            >
                <div className="offering-media">
                    {service.media?.[0] ? (
                        <img src={service.media[0]} alt={service.name} loading="lazy" />
                    ) : (
                        <div className="offering-media-empty">
                            <i className="fas fa-image"></i>
                        </div>
                    )}
                    {discount && (
                        <span className="offering-badge">{discount}% off</span>
                    )}
                </div>

                <div className="offering-info">
                    <h3 className="offering-name">{service.name}</h3>

                    <div className="offering-price">
                        <span className="offering-price-main">{price.main}</span>
                        {price.original && (
                            <span className="offering-price-original">{price.original}</span>
                        )}
                        {bundleQty > 1 && (
                            <span className="offering-bundle">{bundleQty}x</span>
                        )}
                    </div>

                    <div className="offering-meta">
                        {durationLabel && <span>{durationLabel}</span>}
                        {durationLabel && bundleQty > 1 && <span className="offering-meta-dot">·</span>}
                        {bundleQty > 1 && <span>{bundleQty} sessions</span>}
                        {!durationLabel && bundleQty === 1 && <span>Single session</span>}
                    </div>

                    {service.description && (
                        <p className="offering-description">{service.description}</p>
                    )}

                    <span className="offering-action">
                        <i className={`fas ${isBooking ? 'fa-calendar-check' : 'fa-shopping-cart'}`}></i>
                        {isBooking ? 'Book now' : 'Buy now'}
                    </span>
                </div>
            </div>
        );
    };

    return (
        <div className="op-root">
            <Helmet>
                <title>{serverName ? `${serverName} | Verca` : 'Offerings | Verca'}</title>
                <meta property="og:title" content={`${serverName || 'Offerings'} | Verca`} />
                <meta property="og:description" content={business?.description || `Browse services offered by ${serverName || 'this business'}`} />
                {business?.avatar && <meta property="og:image" content={business.avatar} />}
                <meta property="og:type" content="website" />
            </Helmet>

            <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css" />
            <link href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap" rel="stylesheet" />

            {/* Nav — the wordmark hands off to the business branding on scroll */}
            <nav className={`op-nav${navBranded ? ' branded' : ''}`}>
                <div className="op-nav-inner">
                    <div
                        className="op-nav-slot op-logo"
                        onClick={() => navigate('/')}
                        role="button"
                        tabIndex={navBranded ? -1 : 0}
                        aria-hidden={navBranded}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                navigate('/');
                            }
                        }}
                    >
                        <span className="op-logo-text">Verca</span>
                    </div>

                    {renderNavBrand()}
                </div>
            </nav>

            {/* Main content */}
            <main className="op-main">
                <div className="op-container">
                    {loading ? (
                        <div className="op-loading">
                            <div className="op-spinner"></div>
                            <p>Loading offerings...</p>
                        </div>
                    ) : services.length > 0 ? (
                        <>
                            {renderBusinessHeader()}

                            {/* Search + categories */}
                            <div className="op-toolbar">
                                <div className="op-search">
                                    <i className="fas fa-search op-search-icon"></i>
                                    <input
                                        type="text"
                                        className="op-search-input"
                                        placeholder="Search services"
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Escape') setQuery(''); }}
                                        aria-label="Search services"
                                    />
                                    {query && (
                                        <button
                                            className="op-search-clear"
                                            onClick={() => setQuery('')}
                                            aria-label="Clear search"
                                        >
                                            <i className="fas fa-times"></i>
                                        </button>
                                    )}
                                </div>

                                {categories.length > 0 && (
                                    <div className={`op-cats-wrap${catScroll.left ? ' has-left' : ''}${catScroll.right ? ' has-right' : ''}`}>
                                        <button
                                            type="button"
                                            className="op-cats-arrow left"
                                            onClick={() => scrollCats(-1)}
                                            aria-label="Scroll categories left"
                                        >
                                            <i className="fas fa-chevron-left"></i>
                                        </button>

                                        <div className="op-cats-rail">
                                            <div className="op-cats" ref={catsRef} role="tablist" aria-label="Service categories">
                                                <button
                                                    role="tab"
                                                    aria-selected={activeCategory === ALL_CATEGORY}
                                                    className={`op-cat ${activeCategory === ALL_CATEGORY ? 'active' : ''}`}
                                                    onClick={() => handleCategorySelect(ALL_CATEGORY)}
                                                >
                                                    All
                                                    <span className="op-cat-count">{services.length}</span>
                                                </button>
                                                {categories.map((cat) => (
                                                    <button
                                                        key={cat.name}
                                                        role="tab"
                                                        aria-selected={activeCategory === cat.name}
                                                        className={`op-cat ${activeCategory === cat.name ? 'active' : ''}`}
                                                        onClick={() => handleCategorySelect(cat.name)}
                                                    >
                                                        {cat.name}
                                                        <span className="op-cat-count">{cat.count}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            className="op-cats-arrow right"
                                            onClick={() => scrollCats(1)}
                                            aria-label="Scroll categories right"
                                        >
                                            <i className="fas fa-chevron-right"></i>
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Everything below the toolbar crossfades on category change */}
                            <div className={`op-results ${fadeState === 'out' ? 'out' : 'in'}`}>
                                {isFiltering && filteredServices.length > 0 && (
                                    <p className="op-result-count">
                                        {filteredServices.length} of {services.length} services
                                    </p>
                                )}

                                {filteredServices.length > 0 ? (
                                    <div className="op-groups">
                                        {groupedServices.map(([category, items]) => (
                                            <section key={category} className="op-group">
                                                {showGroupHeadings && (
                                                    <h2 className="op-group-title">{category}</h2>
                                                )}
                                                <div className="op-grid">
                                                    {items.map((service, index) => renderCard(service, index))}
                                                </div>
                                            </section>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="op-empty">
                                        <div className="op-empty-icon"><i className="fas fa-search"></i></div>
                                        <h2>No matching services</h2>
                                        <p>
                                            {query.trim()
                                                ? `Nothing here matches "${query.trim()}". Try a different word or another category.`
                                                : 'This category is empty. Pick another one to keep browsing.'}
                                        </p>
                                        <button className="op-btn-secondary" onClick={clearFilters}>Show all services</button>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="op-error">
                            <div className="op-error-icon"><i className="fas fa-store"></i></div>
                            <h2>No offerings yet</h2>
                            <p>This business hasn't listed any services</p>
                            <button className="op-btn-secondary" onClick={() => navigate('/')}>Back to home</button>
                        </div>
                    )}
                </div>
            </main>

            <style>{`
                /* ── Reset & Base ── */
                *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

                /* Monochrome palette (matches ServicesPage):
                   #FFFFFF page · #F6F6F6 surface · #EBEBEB hover · #E0E0E0 hairline
                   #000000 ink · #767676 secondary · #A3A3A3 tertiary */
                .op-root {
                    min-height: 100vh;
                    background: #FFFFFF;
                    font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
                    color: #000000;
                    -webkit-font-smoothing: antialiased;
                }

                /* ── Nav ──
                   The wordmark and the business branding share one slot on the
                   left and crossfade past each other: the wordmark lifts away as
                   the branding rises into place, so the swap reads as a single
                   movement rather than two independent fades. Both are absolutely
                   positioned, so the nav height never shifts and the sticky
                   toolbar underneath stays put. */
                .op-nav {
                    position: fixed; top: 0; left: 0; right: 0; z-index: 100;
                    padding: 1.25rem;
                    background: rgba(255, 255, 255, 0.85);
                    backdrop-filter: blur(16px);
                    border-bottom: 1px solid #EDEDED;
                }
                .op-nav-inner {
                    position: relative;
                    max-width: 1120px; margin: 0 auto;
                    min-height: 34px;
                }
                .op-nav-slot {
                    position: absolute; left: 0; top: 50%;
                    display: flex; align-items: center; gap: 10px;
                    max-width: 100%; min-width: 0;
                    padding: 0; background: transparent; border: none;
                    font-family: 'DM Sans', sans-serif;
                    text-align: left; cursor: pointer;
                    transition: opacity 280ms cubic-bezier(0.32, 0, 0.24, 1),
                                transform 280ms cubic-bezier(0.32, 0, 0.24, 1);
                }
                .op-nav-slot:focus-visible { outline: 2px solid #000000; outline-offset: 6px; border-radius: 6px; }

                /* Wordmark: visible at the top of the page, lifts out on scroll. */
                .op-logo { transform: translateY(-50%); opacity: 1; }
                .op-logo:hover { opacity: 0.65; }
                .op-logo-text { font-size: 1.35rem; font-weight: 700; letter-spacing: -0.5px; color: #000000; }
                .op-nav.branded .op-logo {
                    opacity: 0;
                    transform: translateY(calc(-50% - 10px));
                    pointer-events: none;
                }

                /* Business branding: waits just below, rises in as you scroll. */
                .op-nav-brand {
                    opacity: 0;
                    transform: translateY(calc(-50% + 10px));
                    pointer-events: none;
                }
                .op-nav.branded .op-nav-brand {
                    opacity: 1;
                    transform: translateY(-50%);
                    pointer-events: auto;
                }
                .op-nav-brand:hover { opacity: 0.65; }
                .op-nav-brand-avatar {
                    width: 34px; height: 34px; flex-shrink: 0;
                    border-radius: 50%;
                    object-fit: cover; object-position: center;
                    background: #F6F6F6;
                    display: block;
                }
                .op-nav-brand-avatar-fallback {
                    display: flex; align-items: center; justify-content: center;
                    border: 1px solid #E6E6E6;
                    font-size: 15px; font-weight: 700; color: #A3A3A3;
                    letter-spacing: -0.3px; user-select: none;
                }
                .op-nav-brand-name {
                    min-width: 0;
                    font-size: 1.15rem; font-weight: 700; color: #000000;
                    letter-spacing: -0.5px; line-height: 1.2;
                    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
                }

                /* ── Main ── */
                .op-main { padding-top: 110px; min-height: 100vh; background: #FFFFFF; }
                .op-container { max-width: 1120px; margin: 0 auto; padding: 0 1.25rem 4rem; }

                /* ── Business header ──
                   Circular avatar with the name set large beside it, blurb full
                   width underneath, contact details as chips below that. */
                .op-brand { margin-bottom: 26px; animation: fadeSlideIn 0.4s ease; }
                .op-brand-main { display: flex; align-items: center; gap: 24px; }

                .op-brand-avatar {
                    width: 112px; height: 112px; flex-shrink: 0;
                    border-radius: 50%;
                    object-fit: cover; object-position: center;
                    background: #F6F6F6;
                    display: block;
                }
                .op-brand-avatar-fallback {
                    display: flex; align-items: center; justify-content: center;
                    border: 1px solid #E6E6E6;
                    font-size: 2.5rem; font-weight: 700; color: #A3A3A3;
                    letter-spacing: -1px; user-select: none;
                }

                .op-brand-name {
                    min-width: 0;
                    font-size: 3rem; font-weight: 700; color: #000000;
                    letter-spacing: -1.5px; line-height: 1.1;
                    overflow-wrap: anywhere;
                }

                /* Blurb sits below the avatar row and runs the full column, with
                   the toggle inline at the end of the last line. */
                .op-brand-description {
                    font-size: 1.25rem; color: #2B2B2B; line-height: 1.45;
                    margin-top: 22px; max-width: 72ch;
                    white-space: pre-line; overflow-wrap: anywhere;
                }
                .op-bio-toggle {
                    display: inline; padding: 0;
                    background: transparent; border: none;
                    color: #000000; font-size: inherit; font-weight: 500;
                    font-family: 'DM Sans', sans-serif; line-height: inherit;
                    text-decoration: underline; text-underline-offset: 3px;
                    cursor: pointer; transition: opacity 0.2s;
                }
                .op-bio-toggle:hover { opacity: 0.6; }
                .op-bio-toggle:focus-visible { outline: 2px solid #000000; outline-offset: 3px; border-radius: 4px; }

                /* Contact chips — phone dials, location opens maps. */
                .op-brand-contact {
                    display: flex; flex-wrap: wrap; gap: 10px;
                    margin-top: 20px;
                }
                .op-contact-chip {
                    display: inline-flex; align-items: center; gap: 9px;
                    max-width: 100%;
                    padding: 11px 16px; border-radius: 999px;
                    background: #FFFFFF; border: 1px solid #E0E0E0;
                    color: #1A1A1A; font-size: 14px; font-weight: 600;
                    font-family: 'DM Sans', sans-serif; line-height: 1;
                    text-decoration: none; cursor: pointer;
                    transition: background 0.2s, border-color 0.2s, color 0.2s;
                }
                .op-contact-chip span {
                    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
                }
                .op-contact-chip i { font-size: 12px; color: #767676; flex-shrink: 0; transition: color 0.2s; }
                .op-contact-chip:hover { background: #000000; border-color: #000000; color: #FFFFFF; }
                .op-contact-chip:hover i { color: #FFFFFF; }
                .op-contact-chip:focus-visible { outline: 2px solid #000000; outline-offset: 2px; }

                /* ── Toolbar (search + categories) ── */
                .op-toolbar {
                    position: sticky; top: 74px; z-index: 90;
                    background: #FFFFFF;
                    padding: 14px 0 0;
                    border-bottom: 1px solid #EDEDED;
                    animation: fadeSlideIn 0.4s ease;
                }

                .op-search {
                    display: flex; align-items: center;
                    background: #FFFFFF;
                    border: 1px solid #D6D6D6;
                    border-radius: 14px;
                    padding: 0 14px;
                    transition: border-color 0.2s, box-shadow 0.2s;
                }
                .op-search:focus-within { border-color: #000000; box-shadow: 0 0 0 1px #000000; }
                .op-search-icon { color: #A3A3A3; font-size: 15px; flex-shrink: 0; }
                .op-search-input {
                    flex: 1; min-width: 0;
                    border: none; outline: none; background: transparent;
                    padding: 15px 12px; font-size: 16px; color: #000000;
                    font-family: 'DM Sans', sans-serif;
                }
                .op-search-input::placeholder { color: #A3A3A3; }
                .op-search-clear {
                    width: 28px; height: 28px; border-radius: 50%;
                    border: none; background: #EBEBEB; color: #444444;
                    cursor: pointer; flex-shrink: 0;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 12px; transition: background 0.15s, color 0.15s;
                }
                .op-search-clear:hover { background: #000000; color: #FFFFFF; }

                /* ── Category rail ──
                   Bleeds to the container edges so the row scrolls edge-to-edge
                   on touch. The edge fade only appears on the side that actually
                   has more to scroll, and on pointer devices an arrow sits over
                   each live fade. */
                .op-cats-wrap { position: relative; margin: 12px -1.25rem 0; }

                .op-cats-wrap.has-left .op-cats-rail {
                    -webkit-mask-image: linear-gradient(to right, transparent 0, #000 2.5rem, #000 100%);
                    mask-image: linear-gradient(to right, transparent 0, #000 2.5rem, #000 100%);
                }
                .op-cats-wrap.has-right .op-cats-rail {
                    -webkit-mask-image: linear-gradient(to right, #000 0, #000 calc(100% - 2.5rem), transparent 100%);
                    mask-image: linear-gradient(to right, #000 0, #000 calc(100% - 2.5rem), transparent 100%);
                }
                .op-cats-wrap.has-left.has-right .op-cats-rail {
                    -webkit-mask-image: linear-gradient(to right, transparent 0, #000 2.5rem, #000 calc(100% - 2.5rem), transparent 100%);
                    mask-image: linear-gradient(to right, transparent 0, #000 2.5rem, #000 calc(100% - 2.5rem), transparent 100%);
                }

                /* top: 50% - 7px re-centers on the pills, discounting the rail's
                   14px bottom padding. */
                .op-cats-arrow {
                    position: absolute;
                    top: calc(50% - 7px);
                    transform: translateY(-50%);
                    width: 34px; height: 34px;
                    display: none;
                    align-items: center; justify-content: center;
                    border-radius: 50%;
                    background: #FFFFFF;
                    border: 1px solid #E0E0E0;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.10);
                    color: #000000; font-size: 12px;
                    cursor: pointer; z-index: 3;
                    opacity: 0; pointer-events: none;
                    transition: opacity 0.2s ease, background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
                }
                .op-cats-arrow.left { left: 0.35rem; }
                .op-cats-arrow.right { right: 0.35rem; }
                .op-cats-arrow:hover { background: #000000; border-color: #000000; color: #FFFFFF; }
                .op-cats-arrow:focus-visible { outline: 2px solid #000000; outline-offset: 2px; }

                /* Pointer devices only — touch users swipe, so the arrows would
                   just sit on top of pills. */
                @media (hover: hover) and (min-width: 901px) {
                    .op-cats-arrow { display: flex; }
                    .op-cats-wrap.has-left  .op-cats-arrow.left  { opacity: 1; pointer-events: auto; }
                    .op-cats-wrap.has-right .op-cats-arrow.right { opacity: 1; pointer-events: auto; }
                }

                .op-cats {
                    display: flex; align-items: center; gap: 10px;
                    overflow-x: auto; overflow-y: hidden;
                    padding: 0 1.25rem 14px;
                    scroll-behavior: smooth;
                    scroll-padding-inline: 1.25rem;
                    -webkit-overflow-scrolling: touch;
                    scrollbar-width: none;
                }
                .op-cats::-webkit-scrollbar { display: none; }
                .op-cat {
                    display: inline-flex; align-items: center; gap: 8px;
                    flex-shrink: 0; white-space: nowrap;
                    padding: 12px 20px; border-radius: 999px;
                    background: #FFFFFF; border: 1px solid #E0E0E0;
                    color: #1A1A1A; font-size: 16px; font-weight: 600;
                    letter-spacing: -0.2px; line-height: 1;
                    font-family: 'DM Sans', sans-serif;
                    cursor: pointer;
                    transition: background 0.22s ease, border-color 0.22s ease, color 0.22s ease;
                }
                .op-cat:hover { background: #F6F6F6; border-color: #B8B8B8; }
                .op-cat.active { background: #000000; border-color: #000000; color: #FFFFFF; }
                .op-cat:focus-visible { outline: 2px solid #000000; outline-offset: 2px; }
                .op-cat-count {
                    font-size: 14px; font-weight: 600; color: #767676;
                    background: #F2F2F2; border-radius: 999px; padding: 3px 9px;
                    line-height: 1;
                    transition: background 0.22s ease, color 0.22s ease;
                }
                .op-cat.active .op-cat-count { background: rgba(255,255,255,0.2); color: #FFFFFF; }

                /* ── Results crossfade (mirrors .sp-animated in ServicesPage) ── */
                .op-results {
                    transition: opacity 320ms cubic-bezier(0.32, 0, 0.24, 1),
                                transform 320ms cubic-bezier(0.32, 0, 0.24, 1);
                    will-change: opacity, transform;
                }
                .op-results.in { opacity: 1; transform: translateY(0) scale(1); }
                /* Outgoing content leaves faster than the incoming fade, which
                   reads as a crossfade rather than a hard swap. */
                .op-results.out {
                    opacity: 0;
                    transform: translateY(6px) scale(0.995);
                    transition-duration: 200ms;
                    transition-timing-function: cubic-bezier(0.4, 0, 1, 1);
                }

                .op-result-count {
                    font-size: 14px; color: #767676; font-weight: 500;
                    margin: 18px 0 -10px;
                }

                /* ── Loading ── */
                .op-loading {
                    display: flex; flex-direction: column; align-items: center;
                    justify-content: center; min-height: 50vh; gap: 1.25rem;
                }
                .op-loading p { color: #767676; font-size: 0.95rem; }
                .op-spinner {
                    width: 40px; height: 40px;
                    border: 2px solid #E6E6E6;
                    border-top-color: #000000; border-radius: 50%;
                    animation: spin 0.8s linear infinite;
                }
                @keyframes spin { to { transform: rotate(360deg); } }

                /* ── Empty / Error states ── */
                .op-error, .op-empty {
                    display: flex; flex-direction: column; align-items: center;
                    justify-content: center; text-align: center;
                }
                .op-error { min-height: 50vh; animation: fadeSlideIn 0.35s ease; }
                .op-empty { padding: 4rem 0 3rem; }
                .op-error-icon, .op-empty-icon {
                    width: 72px; height: 72px; border-radius: 20px;
                    background: #F6F6F6;
                    display: flex; align-items: center; justify-content: center; margin-bottom: 1.5rem;
                }
                .op-error-icon i, .op-empty-icon i { font-size: 1.75rem; color: #A3A3A3; }
                .op-error h2, .op-empty h2 {
                    font-size: 1.5rem; font-weight: 700; margin-bottom: 0.5rem;
                    letter-spacing: -0.5px; color: #000000;
                }
                .op-error p, .op-empty p {
                    color: #767676; margin-bottom: 1.75rem; max-width: 340px; line-height: 1.55;
                }
                .op-btn-secondary {
                    display: flex; align-items: center; justify-content: center;
                    padding: 15px 26px; border-radius: 16px;
                    font-size: 15px; font-weight: 600; font-family: 'DM Sans', sans-serif;
                    background: #FFFFFF; color: #000000;
                    border: 1px solid #D6D6D6; cursor: pointer; transition: all 0.2s;
                }
                .op-btn-secondary:hover { background: #F6F6F6; border-color: #B8B8B8; }
                .op-btn-secondary:focus-visible { outline: 2px solid #000000; outline-offset: 2px; }

                @keyframes fadeSlideIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                /* ── Category groups ── */
                .op-groups { padding-top: 28px; }
                .op-group + .op-group { margin-top: 36px; }
                .op-group-title {
                    font-size: 1.15rem; font-weight: 700; color: #000000;
                    letter-spacing: -0.4px; margin-bottom: 14px;
                }

                /* ── Product grid ──
                   grid-auto-rows: 1fr keeps every row the same height, and the
                   card stretches to fill its cell, so all cards are identical in
                   size no matter how long the name or description runs. */
                .op-grid {
                    display: grid;
                    grid-template-columns: repeat(3, minmax(0, 1fr));
                    grid-auto-rows: 1fr;
                    gap: 18px;
                    align-items: stretch;
                }

                .offering-card {
                    height: 100%;
                    background: #FFFFFF;
                    border: 1px solid #E6E6E6;
                    border-radius: 16px;
                    overflow: hidden;
                    cursor: pointer;
                    display: flex; flex-direction: column;
                    transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
                }
                .offering-card:hover {
                    border-color: #C4C4C4;
                    box-shadow: 0 4px 16px rgba(0,0,0,0.06);
                    transform: translateY(-2px);
                }
                .offering-card:focus-visible { outline: 2px solid #000000; outline-offset: 2px; }

                /* ── Card media ──
                   Every tile is a strict square of identical size: the ::before
                   reserves a 1:1 box (works even where aspect-ratio isn't
                   supported) and the image is absolutely positioned to fill it,
                   so portrait, landscape, and tiny source images all render at
                   exactly the same dimensions. */
                .offering-media {
                    position: relative;
                    width: 100%;
                    flex-shrink: 0;
                    overflow: hidden;
                    background: #F6F6F6;
                    border-bottom: 1px solid #EDEDED;
                }
                .offering-media::before {
                    content: ''; display: block; padding-top: 100%;
                }
                .offering-media img,
                .offering-media-empty {
                    position: absolute; inset: 0;
                    width: 100%; height: 100%;
                }
                .offering-media img {
                    object-fit: cover; object-position: center; display: block;
                }
                .offering-media-empty {
                    display: flex; align-items: center; justify-content: center;
                    color: #C4C4C4; font-size: 1.75rem;
                }
                .offering-badge {
                    position: absolute; top: 10px; right: 10px;
                    background: #000000; color: #FFFFFF;
                    font-size: 11px; font-weight: 700; letter-spacing: 0.3px;
                    padding: 5px 9px; border-radius: 8px;
                }

                /* ── Card details ──
                   The info column fills the remaining height and the action sits
                   on margin-top:auto, so the buttons line up across every card. */
                .offering-info {
                    flex: 1; min-height: 0;
                    padding: 16px 16px 18px;
                    display: flex; flex-direction: column; gap: 10px;
                }
                .offering-name {
                    font-size: 15px; font-weight: 700; color: #000000;
                    letter-spacing: -0.2px; line-height: 1.35;
                    /* Reserve two lines so prices align even with a 1-line name */
                    min-height: calc(2 * 1.35em);
                    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
                    overflow: hidden;
                }
                .offering-price { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
                .offering-price-main { font-size: 1.05rem; font-weight: 700; color: #000000; letter-spacing: -0.3px; }
                .offering-price-original {
                    font-size: 13px; color: #A3A3A3; text-decoration: line-through;
                }
                .offering-bundle {
                    font-size: 11px; font-weight: 700; color: #000000;
                    background: #EDEDED; border-radius: 6px; padding: 2px 7px;
                }

                .offering-meta {
                    display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
                    font-size: 12px; color: #767676; font-weight: 500;
                }
                .offering-meta-dot { color: #C4C4C4; }

                .offering-description {
                    font-size: 13px; color: #767676; line-height: 1.5;
                    white-space: pre-line;
                    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
                    overflow: hidden;
                }

                /* margin-top:auto pushes the button to the bottom of the card so
                   the buttons line up across the row; padding-top on top of the
                   auto margin guarantees breathing room even on a card whose text
                   fills the whole column. */
                .offering-action {
                    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
                    margin-top: auto; padding: 15px 18px; border-radius: 13px;
                    background: #000000; color: #FFFFFF;
                    font-size: 14px; font-weight: 600; letter-spacing: 0.2px;
                    line-height: 1;
                    transition: background 0.2s;
                }
                .offering-card:hover .offering-action { background: #1F1F1F; }
                .offering-action i { font-size: 13px; }

                @media (prefers-reduced-motion: reduce) {
                    .op-brand, .op-toolbar, .op-error { animation: none; }
                    .op-results { transition-duration: 1ms; }
                    .offering-card:hover { transform: none; }
                    .op-cats { scroll-behavior: auto; }
                    .op-cats-arrow { transition: none; }
                    /* Keep the branding swap, drop the travel: opacity only, and
                       hold the vertical centering so nothing jumps. */
                    .op-nav-slot { transition: opacity 1ms linear; }
                    .op-nav.branded .op-logo,
                    .op-nav-brand { transform: translateY(-50%); }
                }

                /* ── Responsive ── */
                /* 3 up holds down to tablet width; below ~600px three cards would
                   be too narrow to read, so it drops to 2. */
                @media (max-width: 900px) {
                    .op-brand-avatar { width: 96px; height: 96px; }
                    .op-brand-name { font-size: 2.25rem; letter-spacing: -1px; }
                    .op-brand-description { font-size: 1.125rem; }

                    .op-grid { gap: 14px; }
                    .offering-info { padding: 14px 14px 16px; }
                }
                @media (max-width: 600px) {
                    .op-container { padding: 0 1.25rem 3rem; }

                    .op-nav-brand-avatar { width: 30px; height: 30px; }
                    .op-nav-brand-avatar-fallback { font-size: 13px; }
                    .op-nav-brand-name { font-size: 1.05rem; letter-spacing: -0.4px; }

                    .op-brand-main { gap: 16px; }
                    .op-brand-avatar { width: 72px; height: 72px; }
                    .op-brand-avatar-fallback { font-size: 1.65rem; }
                    .op-brand-name { font-size: 1.65rem; letter-spacing: -0.6px; }
                    .op-brand-description { font-size: 1rem; margin-top: 16px; }
                    .op-brand-contact { margin-top: 16px; gap: 8px; }
                    .op-contact-chip { padding: 10px 14px; font-size: 13px; }

                    .op-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
                    .offering-info { padding: 13px 13px 15px; }
                    .offering-name { font-size: 14px; }
                    .offering-price-main { font-size: 1rem; }
                    .offering-action { padding: 14px 14px; font-size: 13px; }
                }
            `}</style>
        </div>
    );
};

export default OfferingsPage